import { supabase, updateSupabaseSessionToken } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { User } from "./types";
import { savePackerRelations, determineUserRole, recordLogoutTimestamp } from "./authUtils";

// Enhanced login function with hybrid auth support
export const hybridLoginUser = async (
  username: string, 
  password: string, 
  packers: string[] = [],
  setUser: (user: User | null) => void,
  setIsAuthenticated: (isAuth: boolean) => void,
  setSelectedPackers: (packers: string[]) => void
): Promise<boolean> => {
  try {
    console.log("=== HYBRID LOGIN PROCESS START ===");
    console.log("Step 1: Input validation");
    console.log("Username:", username);
    console.log("Password length:", password?.length || 0);
    console.log("Packers:", packers);
    
    if (!username || !password) {
      console.error("Step 1 FAILED: Missing username or password");
      toast.error("Vul zowel gebruikersnaam als wachtwoord in");
      return false;
    }
    
    console.log("Step 2: Calling backend authentication...");
    console.log("RPC Parameters:", { p_username: username, p_password: '[HIDDEN]' });
    
    // Debug: Check if we can reach the function at all
    console.log("Testing RPC function availability...");
    
    const { data: userData, error: userError } = await supabase
      .rpc('authenticate_user', {
        p_username: username,
        p_password: password
      });
    
    console.log("=== DETAILED RPC RESPONSE ===");
    console.log("Raw userData:", userData);
    console.log("Raw userError:", userError);
    console.log("userData type:", typeof userData);
    console.log("userData is array:", Array.isArray(userData));
    console.log("userData length:", userData?.length);
    console.log("userError details:", {
      message: userError?.message,
      details: userError?.details,
      hint: userError?.hint,
      code: userError?.code
    });
    
    // If we got an error, let's check if it's a permissions issue
    if (userError) {
      console.log("=== RPC ERROR ANALYSIS ===");
      console.log("Error code:", userError.code);
      console.log("Is this a permissions error?", userError.message?.includes('permission') || userError.message?.includes('access'));
      console.log("Is this a function not found error?", userError.message?.includes('function') || userError.message?.includes('does not exist'));
    }
    
    console.log("Step 2 RESULT:", { 
      userData, 
      userError,
      userCount: userData?.length || 0,
      userDataType: typeof userData,
      userErrorMessage: userError?.message
    });
    
    if (userError) {
      console.error("Step 2 FAILED: Backend authentication error:", userError);
      toast.error("Er is een fout opgetreden bij het inloggen");
      return false;
    }
    
    if (!userData || userData.length === 0) {
      console.error("Step 2 FAILED: No user returned from backend authentication");
      console.log("This means either username doesn't exist or password is wrong");
      
      // Log failed login attempt
      await supabase.rpc('log_failed_login', {
        p_username: username,
        p_failure_reason: 'invalid_credentials'
      });
      
      toast.error("Gebruikersnaam of wachtwoord is incorrect");
      return false;
    }
    
    console.log("Step 3: Processing user data");
    const user = userData[0];
    
    // Check if legacy password reset is required
    if (user.needs_password_change) {
      console.log("Legacy password detected - redirecting to registration");
      toast.error("Je account gebruikt nog een standaardwachtwoord. Registreer opnieuw om toegang te krijgen.", {
        duration: 6000,
      });
      const encodedName = encodeURIComponent(user.naam || '');
      window.location.href = `/register?naam=${encodedName}`;
      return false;
    }

    // Check if user needs to register first
    if (user.needs_registration) {
      console.log("User not registered - redirecting to registration");
      toast.error("Je moet je eerst registreren voordat je kunt inloggen.", {
        duration: 6000,
      });
      const encodedName = encodeURIComponent(user.naam || '');
      window.location.href = `/register?naam=${encodedName}`;
      return false;
    }
    console.log("User data:", {
      id: user.id,
      username: user.username,
      naam: user.naam,
      functie: user.functie,
      active: user.active
    });
    
    if (!user.active) {
      console.error("Step 3 FAILED: User account is not active");
      toast.error("Dit account is niet actief");
      return false;
    }
    
    console.log("Step 3 SUCCESS: User data processed successfully");
    
    // Determine role based on functie
    const role = determineUserRole((user as any).role || null, user.functie);
    
    // Create user object
    const loggedInUser: User = {
      id: user.id,
      username: user.username,
      role: role,
      name: user.roepnaam || user.naam.split(' ')[0],
      company: (user.company as "luijten_vvz" | "decoprof") || "luijten_vvz",
    };

    // --- HYBRID AUTH INTEGRATION ---
    console.log("Starting hybrid auth session creation...");
    
    // 1. Update last login timestamp
    await supabase
      .from('medewerkers')
      .update({ last_login: new Date().toISOString() })
      .eq('id', user.id);

    // 2. Check if user has Supabase account linked
    const { data: medewerkerData } = await supabase
      .from('medewerkers')
      .select('supabase_user_id, email')
      .eq('id', user.id)
      .single();

    let supabaseUserId = medewerkerData?.supabase_user_id;

    // 3. Supabase Auth integration is intentionally disabled.
    // This project uses custom session auth via auth_sessions + x-session-token.
    // Previously a shared default password was used here, which is a security risk.
    // Custom session creation below is sufficient; supabaseUserId stays null.

    // 4. Enforce max 3 sessions per user - deactivate oldest if needed
    try {
      const { data: activeSessions } = await supabase
        .from('auth_sessions')
        .select('id, created_at')
        .eq('medewerker_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: true });

      if (activeSessions && activeSessions.length >= 3) {
        const sessionsToDeactivate = activeSessions.slice(0, activeSessions.length - 2);
        for (const oldSession of sessionsToDeactivate) {
          await supabase
            .from('auth_sessions')
            .update({ is_active: false })
            .eq('id', oldSession.id);
        }
        console.log(`Deactivated ${sessionsToDeactivate.length} old sessions (limit: 3)`);
      }
    } catch (limitError) {
      console.error("Error enforcing session limit:", limitError);
    }

    // 5. Create auth session for database access
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 8); // 8 hour session

    const sessionToken = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    try {
      const { data: sessionData, error: sessionError } = await supabase
        .from('auth_sessions')
        .insert({
          medewerker_id: user.id,
          supabase_user_id: supabaseUserId,
          expires_at: expiresAt.toISOString(),
          session_token: sessionToken,
          user_agent: navigator.userAgent,
          ip_address: null,
          last_activity: new Date().toISOString()
        })
        .select()
        .single();

      if (!sessionError && sessionData) {
        console.log("Created auth session:", sessionData.id);
        
        // Store session token for persistence
        localStorage.setItem('auth_session_token', sessionToken);
        localStorage.setItem('auth_session_expires', expiresAt.toISOString());
        updateSupabaseSessionToken(sessionToken);
      }
    } catch (sessionError) {
      console.error("Error creating auth session:", sessionError);
      // Continue without session - user can still use app
    }

    // --- END HYBRID AUTH INTEGRATION ---

    // Create login log entry
    const { error: logError } = await supabase
      .from('login_logs')
      .insert({
        user_id: user.id,
        user_name: user.naam,
        packers: JSON.stringify(packers),
        user_agent: navigator.userAgent,
        ip_address: null
      });

    if (logError) {
      console.error("Error creating login log:", logError);
      toast.warning("Kon loginlog niet opslaan");
    }

    // Log successful login as security event
    await supabase.rpc('log_security_event', {
      p_event_type: 'successful_login',
      p_user_id: user.id,
      p_user_name: user.naam,
      p_details: { 
        packers: packers, 
        user_agent: navigator.userAgent,
        has_supabase_auth: !!supabaseUserId,
        hybrid_session: true
      },
      p_severity: 'info'
    });
    
    // Set user and authentication state
    setUser(loggedInUser);
    setIsAuthenticated(true);
    
    // Save selected packers if provided
    if (packers && packers.length > 0) {
      setSelectedPackers(packers);
      localStorage.setItem('selectedPackers', JSON.stringify(packers));
      
      // Save the packer relations to the database
      await savePackerRelations(loggedInUser.id, packers, loggedInUser.name);
    } else {
      setSelectedPackers([]);
      localStorage.removeItem('selectedPackers');
    }
    
    const displayName = user.roepnaam || user.naam.split(' ')[0];
    toast.success(`Welkom ${displayName}, je bent succesvol ingelogd!`);
    console.log("Hybrid login completed successfully");
    return true;
  } catch (error) {
    console.error("Hybrid login error:", error);
    toast.error("Er is een fout opgetreden bij het inloggen");
    return false;
  }
};

// Enhanced logout function with hybrid auth support
export const hybridLogoutUser = async (
  user: User | null,
  setUser: (user: User | null) => void,
  setIsAuthenticated: (isAuth: boolean) => void,
  setSelectedPackers: (packers: string[]) => void
) => {
  try {
    console.log("Starting hybrid logout");

    // Record logout timestamp before clearing user data
    if (user) {
      await recordLogoutTimestamp(user.id);
    }

    // Deactivate auth session
    const sessionToken = localStorage.getItem('auth_session_token');
    if (sessionToken) {
      await supabase
        .from('auth_sessions')
        .update({ is_active: false })
        .eq('session_token', sessionToken);
      
      localStorage.removeItem('auth_session_token');
      localStorage.removeItem('auth_session_expires');
      updateSupabaseSessionToken(null);
    }

    // Sign out from Supabase Auth if authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      await supabase.auth.signOut();
      console.log("Signed out from Supabase Auth");
    }

    // Clear user data
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('selectedPackers');
    setSelectedPackers([]);
    
    console.log("Hybrid logout completed successfully");
    toast.success("Je bent succesvol uitgelogd");
  } catch (error) {
    console.error("Hybrid logout error:", error);
    // Still clear local state even if database operations fail
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('selectedPackers');
    setSelectedPackers([]);
    toast.success("Je bent succesvol uitgelogd");
  }
};

// Function to check if current session is still valid
export const validateHybridSession = async (): Promise<boolean> => {
  try {
    const sessionToken = localStorage.getItem('auth_session_token');
    const expiresAt = localStorage.getItem('auth_session_expires');
    
    if (!sessionToken || !expiresAt) {
      return false;
    }

    // Check if session is expired
    if (new Date() > new Date(expiresAt)) {
      localStorage.removeItem('auth_session_token');
      localStorage.removeItem('auth_session_expires');
      return false;
    }

    // Check if session is still active in database
    const { data, error } = await supabase
      .from('auth_sessions')
      .select('is_active, expires_at')
      .eq('session_token', sessionToken)
      .eq('is_active', true)
      .single();

    if (error || !data) {
      localStorage.removeItem('auth_session_token');
      localStorage.removeItem('auth_session_expires');
      return false;
    }

    return new Date() < new Date(data.expires_at);
  } catch (error) {
    console.error("Error validating hybrid session:", error);
    return false;
  }
};